<script setup lang="ts">
import { storeToRefs } from 'pinia';
import { CircleCheck, TriangleAlert, CircleX, Info, X } from '@lucide/vue';
import { useToastStore } from '@/stores/toast';

const toastStore = useToastStore();
const { items } = storeToRefs(toastStore);
const ICONS = { success: CircleCheck, warning: TriangleAlert, error: CircleX, info: Info } as const;
</script>

<template>
  <div class="toast-stack" aria-live="polite">
    <TransitionGroup name="toast">
      <div v-for="t in items" :key="t.id" class="toast" :class="`toast-${t.kind}`" role="status">
        <component :is="ICONS[t.kind]" class="toast-icon" />
        <span class="toast-text">{{ t.text }}</span>
        <button class="toast-close" aria-label="Dismiss" @click="toastStore.dismiss(t.id)"><X /></button>
      </div>
    </TransitionGroup>
  </div>
</template>

<style scoped>
.toast-stack {
  position: fixed;
  bottom: 20px;
  right: 20px;
  display: flex;
  flex-direction: column;
  align-items: flex-end;
  gap: 8px;
  z-index: 300;
  pointer-events: none;
  max-width: min(420px, calc(100vw - 32px));
}
.toast {
  pointer-events: auto;
  display: flex;
  align-items: flex-start;
  gap: 10px;
  padding: 11px 12px 11px 14px;
  border-radius: var(--radius-lg);
  background: var(--c-surface);
  border: 1px solid var(--c-border);
  box-shadow: var(--shadow-lg);
  color: var(--c-text);
  font-size: var(--fs-sm);
  line-height: 1.45;
  min-width: 240px;
}
.toast-icon { width: 18px; height: 18px; flex-shrink: 0; margin-top: 1px; }
.toast-success .toast-icon { color: var(--c-success-text); }
.toast-warning .toast-icon { color: var(--c-warning-text); }
.toast-error .toast-icon { color: var(--c-danger-text); }
.toast-info .toast-icon { color: var(--c-info); }
.toast-error { border-color: color-mix(in srgb, var(--c-danger) 40%, var(--c-border)); }
.toast-text { flex: 1; overflow-wrap: anywhere; }
.toast-close {
  background: none;
  border: none;
  color: var(--c-text-3);
  cursor: pointer;
  padding: 2px;
  border-radius: 4px;
  display: grid;
  place-items: center;
}
.toast-close:hover { color: var(--c-text); background: var(--c-hover); }
.toast-close svg { width: 14px; height: 14px; }

.toast-enter-active, .toast-leave-active { transition: all 0.22s var(--ease); }
.toast-enter-from { opacity: 0; transform: translateY(10px) scale(0.98); }
.toast-leave-to { opacity: 0; transform: translateX(20px); }

@media (max-width: 640px) {
  .toast-stack {
    left: 12px;
    right: 12px;
    bottom: calc(12px + env(safe-area-inset-bottom));
    max-width: none;
    align-items: stretch;
  }
}
</style>
